import { useMemo } from 'react';

import { orderBy } from 'lodash';

import { UseBaseTable } from 'hooks/useBaseTable';

import { ReportList } from 'api/endPoints/getReportList';

import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { UseReportsTableProvider } from 'components/widgets/ReportsHome/ReportsListTable/ReportsListTable.Provider';

interface Props {
    table: UseBaseTable['table'];
    api: UseReportsTableProvider['api'];
    setSearch: (search: string) => void;
    setSelectedProjects: (projects: ISelectOption<string>[]) => void;
    setSelectedTag: (tag: ISelectOption<number> | null) => void;
    setShowOnlyFavorites: (isShow: boolean) => void;
}

export const useReportListTableFilterLogic = ({
    table,
    setSelectedProjects,
    api,
    setSearch,
    setSelectedTag,
    setShowOnlyFavorites,
}: Props) => {
    const getProjectList = useMemo(() => {
        const projectList: ISelectOption<string>[] = [];
        if (!api.getReportList.data) return projectList;
        const uniqueProjects = new Set<string>();
        api.getReportList.data.forEach((report: ReportList) => {
            report.projContrDetails.forEach((project) => {
                if (!uniqueProjects.has(project.projectName)) {
                    uniqueProjects.add(project.projectName);
                    projectList.push({
                        label: project.projectName,
                        value: project.projectName,
                    });
                }
            });
        });

        const sortedProjectsOptions = orderBy(projectList, ['label'], ['asc']);
        return sortedProjectsOptions;
    }, [api.getReportList.data]);

    const tagOptions = useMemo(() => {
        const options: { value: number; label: string }[] = [];
        if (!api.getTagList.data) return options;
        api.getTagList.data.forEach((tag) => {
            options.push({
                value: tag.id,
                label: tag.name,
            });
        });
        return options;
    }, [api.getTagList.data]);

    const handleClearAll = () => {
        setSearch('');
        setSelectedProjects([]);
        setSelectedTag(null);
        setShowOnlyFavorites(false);
        table.setColumnFilters([]);
    };

    const applyFiltersToTable = (newValue, id: string) => {
        table.setColumnFilters((oldFilters) => {
            const filtersWithoutUpdatedValue = oldFilters.filter((filter) => filter.id !== id);
            return [
                ...filtersWithoutUpdatedValue,
                {
                    id,
                    value: newValue,
                },
            ];
        });
    };

    const handleSearchChange = (search: string) => {
        setSearch(search);
        applyFiltersToTable(search, columnsCommonConfig.reportName.id);
    };

    const handleTagSelectionChange = (selectedTag: ISelectOption<number>) => {
        setSelectedTag(selectedTag);
        applyFiltersToTable(selectedTag?.value, columnsCommonConfig.reportTags.id);
    };

    const handleProjectOnChange = (value: ISelectOption<string>[]) => {
        setSelectedProjects(value);
        applyFiltersToTable(value, columnsCommonConfig.reportProjects.id);
    };

    const handleShowOnlyFavoritesChange = (value: boolean) => {
        setShowOnlyFavorites(value);
        applyFiltersToTable(value, 'isMyFavorite');
    };

    return {
        logic: {
            handleProjectOnChange,
            handleSearchChange,
            handleClearAll,
            getProjectList,
            tagOptions,
            handleTagSelectionChange,
            handleShowOnlyFavoritesChange,
        },
    };
};
